import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/common/DataTable";
import MultiSelect, { MultiSelectOption } from "@/components/common/MultiSelect";
import { Input } from "@/components/ui/input";
import { getDistinctStaffCities, getStaffingUsersWithFilters } from "@/services/user/User";
import { StaffingStaffMember } from "@/types/common/staffMember";
import {
    buildStaffCityLookupFilter,
    buildStaffFilter,
    STAFF_EMP_STATUS_OPTIONS,
    STAFF_ROLE_OPTIONS,
    StaffFilters,
} from "@/utils/staffingUtil";
import { PAGE_SIZE } from "@/utils/constants";
import { useStaffingTable } from "@/hooks/useStaffingTable";
import { useApiClient } from "@/context/ApiContext";
import { staffColumns } from "../columns/StaffingDashboardColumns";

interface StaffTabProps {
    region: string | null;
}

const DEFAULT_FILTERS: StaffFilters = {
    name: '',
    roles: [],
    empStatuses: [],
    cities: [],
};

const StaffTab = ({ region }: StaffTabProps) => {
    const apiClient = useApiClient();
    const [cityOptions, setCityOptions] = useState<MultiSelectOption[]>([]);

    const { data, loading, error, total, filters, applyFilter, handleNameChange, handleParamsChange } =
        useStaffingTable<StaffingStaffMember, StaffFilters>({
            region,
            defaultFilters: DEFAULT_FILTERS,
            buildFilter: buildStaffFilter,
            defaultOrderBy: 'FullName asc',
            fetcher: getStaffingUsersWithFilters,
        });

    const { roles, empStatuses } = filters;
    const cityLookupFilters = useMemo(() => ({ name: '', roles, empStatuses, cities: [] }), [roles, empStatuses]);
    useEffect(() => {
        if (!region) return;
        getDistinctStaffCities(buildStaffCityLookupFilter(region, cityLookupFilters), apiClient)
            .then(res => setCityOptions((res.data as { City: string }[]).map(v => ({ label: v.City, value: v.City }))))
            .catch(() => {});
    }, [region, cityLookupFilters, apiClient]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <Input
                    placeholder="Search by name..."
                    value={filters.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-48"
                />
                <MultiSelect
                    options={STAFF_ROLE_OPTIONS}
                    value={filters.roles}
                    onChange={(roles) => applyFilter({ ...filters, roles })}
                    placeholder="Role"
                    searchPlaceholder="Search roles..."
                />
                <MultiSelect
                    options={STAFF_EMP_STATUS_OPTIONS}
                    value={filters.empStatuses}
                    onChange={(empStatuses) => applyFilter({ ...filters, empStatuses })}
                    placeholder="Status"
                    searchPlaceholder="Search statuses..."
                />
                <MultiSelect
                    options={cityOptions}
                    value={filters.cities}
                    onChange={(cities) => applyFilter({ ...filters, cities })}
                    placeholder="City"
                    searchPlaceholder="Search cities..."
                />
            </div>
            <DataTable
                data={data}
                columns={staffColumns}
                loading={loading}
                error={error ?? undefined}
                serverSide={{ total, pageSize: PAGE_SIZE, onParamsChange: handleParamsChange }}
            />
        </div>
    );
};

export default StaffTab;
